import dateUtils from '@/common/dateUtils';
import fahrdienst452Api from '@/modules/fahrdienst452/fahrdienst452Api';
import tenantSelectionFactory from '@/modules/common/TenantFahrdienstTimetableSelection';

/**
 * @typedef {Object} PerlschurReturnObject
 * @property {Array} umlaeufe - Array of Wagenumläufe provided by the cockpit backend
 * @property {Number} umlauf - ??? used?
 * @property {selectedUmlaufIndex}
 * @property {umlaeufeWithFahrten} - count of umläufe containing filtered fahrten (property) array which is not empty
 * @fahrtenAndUmlaufList {Array} - array of objects composed of fahrten and umlauf properties
 */

const debug = false;

class PerlschnurBuilder {
  constructor({ tenant, timetable, perlschnurJsonByTourHash }) {
    this.timetable = timetable;
    this.perlschnurJsonByTourHash = perlschnurJsonByTourHash;
    this.tenant = tenant;
  }

  /**
   * Prepare row data for the specified Perlschnur (line or track, date, start and end time) based on pre-calculated json.
   * @param {Number} startToMinutesAfterMidnight - Start minutes of tour or null if not selected
   * @param {Number} endMinutesAfterMidnight - End minutes of tour or null if not selected
   * @param {String} line - (=Linie)
   * @param {String} track: (=Kurs)
   * @param {Number} umlauf - Optional umlauf number - if specified and multiple Umläufe get found, this umlauf number is used to select the umlauf
   * @param {String} dateIsoString - day as ISO String
   * @param autoSelectUmlauf - true to indicate that umlauf is selected according to a comparison of times, umlauf, kurs and line (automatic selection). This must be false in case of fahrdienst452 selection, because there are no start/end times.
   *                           In contrary the Mein Dienst can select it according the given time range
   * @returns {PerlschurReturnObject}
   */
  async calculateItems({
    startToMinutesAfterMidnight: startMinutesAfterMidnight = 0,
    endToMinutesAfterMidnight: endMinutesAfterMidnight = 60 * 30,
    line,
    track,
    umlauf,
    blockcode,
    tourHashParam,
    dateIsoString,
    autoSelectUmlauf = false,
  }) {
    console.debug(
      'calculateItems dateIsoString',
      dateIsoString,
      'tourHashParam',
      tourHashParam,
      'track',
      track,
      'line',
      line,
      'umlauf',
      umlauf,
      startMinutesAfterMidnight,
      startMinutesAfterMidnight,
      line,
      track,
      umlauf,
      dateIsoString
    );

    if (!startMinutesAfterMidnight) startMinutesAfterMidnight = 0;
    if (!endMinutesAfterMidnight) endMinutesAfterMidnight = 60 * 29; // see Ticket OP-3463

    if (!(dateIsoString && (line || track || tourHashParam))) {
      console.warn(`Failed to calculate perlschnur because dateIsoString ${dateIsoString}, line ${line} or track ${track} is undefined`);
      return {};
    }

    // find perlschnurJson based on track and date
    let dateAsYYYYMMDD = dateUtils.reformatDateIsoStringAsYYYYMMDD(dateIsoString);
    let searchDate = new Date(Date.parse(dateIsoString));
    let month = searchDate.getMonth();
    let date = searchDate.getDate();
    let year = searchDate.getFullYear();
    let selectionLogic = tenantSelectionFactory[this.tenant];
    if (!selectionLogic) {
      console.debug('kick in with default selection logic for tenant', this.tenant);
      selectionLogic = tenantSelectionFactory.default;
    }
    //let timetableItems = this.timetable.filter((item) => item.day === dateAsYYYYMMDD && item.kurs == track); // track may be a string so use == instead of ===
    let timetableItems = this.timetable.filter((item) => {
      // if ((item.blockcode &&§ item.blockcode.includes(track)) || item.kurs == track) {
      // if (item.kurs == track) {
      // ACHTUNG: Gut prüfen, ob keine Seiteneffekt
      if (selectionLogic({ item, track, line, blockcode, tourHash: tourHashParam })) {
        let d = new Date(Date.parse(item.day));
        let itemDay = d.getDate();
        let itemMonth = d.getMonth();
        let itemYear = d.getFullYear();
        // console.debug(`Compare kurs=${item.kurs} ${item.id}: ${itemDay}.${itemMonth+1}.${itemYear} with ${date}.${month+1}.${year} (${dateIsoString})`)
        // console.debug(`Compare itemDay ${itemDay} === date ${date} && itemMonth ${itemMonth} === month ${month} && itemYear ${itemYear} === year ${year}`)
        return itemDay === date && itemMonth === month && itemYear === year;
      }
      return false;
    });
    // item.day === dateAsYYYYMMDD && item.kurs == track}); // track may be a string so use == instead of ===
    console.debug('Filtered timetableItems for day=', dateAsYYYYMMDD, 'kurs=', track, timetableItems);
    if (timetableItems.length > 1) {
      debug && console.debug(`Found timetableItems for ${dateAsYYYYMMDD} and track=${track} `, JSON.stringify(timetableItems, null, 2));
      console.debug('Found more than one timetable item. See above');
    }
    if (timetableItems.length === 0) {
      console.warn('No PerlschnurJson found');
      return {
        umlaeufe: [],
        umlauf: null,
        umlaeufeWithFahrten: 0,
        selectedUmlaufIndex: 0,
        fahrtenAndUmlaufList: [],
      };
    }
    let { tourHash, checksum } = timetableItems[0];
    let perlschnurData = this.perlschnurJsonByTourHash[`${tourHash}/${checksum}`];

    if (!perlschnurData) {
      debug && console.debug('no perlschnur data in store. Fetch over network/cache and store in perlschnurData');
      let perlschnurResponse = await fahrdienst452Api.fetchPerlschnur(`${tourHash}/${checksum}`);
      perlschnurData = perlschnurResponse.data;
      this.perlschnurJsonByTourHash[`${tourHash}/${checksum}`] = perlschnurData;
    }
    console.debug(`found perlschnurData ${tourHash}/${checksum}`, perlschnurData);
    return this.calculatePerlschnurFromJson({
      perlschnurData,
      startMinutesAfterMidnight,
      endMinutesAfterMidnight,
      track,
      line,
      umlauf,
      autoSelectUmlauf,
    });
  }

  /**
   * Calculates perlschnur data from server-side prepared perlschnur.json
   * @param perlschnurData
   * @param startMinutesAfterMidnight
   * @param endMinutesAfterMidnight
   * @param track
   * @param line
   * @param umlauf
   * @param autoSelectUmlauf - true to indicate that umlauf is selected according to a comparison of times, umlauf, kurs and line (automatic selection). This must be false in case of fahrdienst452 selection, because there are no start/end times.
   *                           In contrary the Mein Dienst can select it according the given time range
   *
   * @returns {{umlauf: *, umlaeufeWithFahrten: number, fahrtenAndUmlaufList: {umlauf, fahrten: *[]}[], selectedUmlaufIndex: number, umlaeufe: ([]|Array|[{ort: {longname: string, zone: number, didok: string, id: number, shortname: string}, abOrt: string, umlauf: number, verlauf: {ort: number, adapter_zielId: number, sort: number, id: number}, start: string, kurs: number, abfahrt: string},{ort: {longname: string, zone: number, didok: string, id: number, shortname: string}, abOrt: string, umlauf: number, verlauf: {ort: number, adapter_zielId: number, sort: number, id: number}, start: string, kurs: number, abfahrt: string}]|[{ort: {longname: string, zone: number, didok: string, id: number, shortname: string}, abOrt: string, umlauf: number, verlauf: {ort: number, adapter_zielId: number, sort: number, id: number}, start: string, kurs: number, abfahrt: string},{ort: {longname: string, zone: number, didok: string, id: number, shortname: string}, abOrt: string, umlauf: number, verlauf: {ort: number, adapter_zielId: number, sort: number, id: number}, start: string, kurs: number, abfahrt: string}]|*)}}
   */
  calculatePerlschnurFromJson({ perlschnurData, startMinutesAfterMidnight, endMinutesAfterMidnight, track, line, umlauf, autoSelectUmlauf = false }) {
    let fahrtenAndUmlaufList;

    if (autoSelectUmlauf && perlschnurData.linien.length > 0 && perlschnurData.umlaeufe.length > 0) {
      console.debug('calculatePerlschnurFromJson in case Umläufe AND linien are defined (see Ticket OP-3463)');
      fahrtenAndUmlaufList = this.buildUmlaufFahrtenArrayByUsingLinesAndUmlaeufe({
        perlschnurData,
        track,
        line,
        startMinutesAfterMidnight,
        endMinutesAfterMidnight,
      });
    } else if (perlschnurData.umlaeufe.length > 0) {
      console.debug('calculatePerlschnurFromJson with perlschnurData.umlauefe.length>0');
      fahrtenAndUmlaufList = this.buildUmlaufFahrtenArrayFromUmlaeufe({
        perlschnurData,
        track,
        startMinutesAfterMidnight,
        endMinutesAfterMidnight,
      });
    } else if (perlschnurData.linien && perlschnurData.linien.length > 0) {
      // In case no Umläufe defined, we filter fahrten by linien
      fahrtenAndUmlaufList = this.buildUmlaufFahrtenArrayByConcatinatingLines({
        perlschnurData,
        track,
        startMinutesAfterMidnight,
        endMinutesAfterMidnight,
      });
    } else {
      // In case no Umläufe defined, we filter by track=kurs
      console.debug('calculatePerlschnurFromJson In case no Umläufe defined, we filter by track=kurs');

      fahrtenAndUmlaufList = this.buildUmlaufFahrtenArrayByFilteringForTrack({
        perlschnurData,
        track,
        startMinutesAfterMidnight,
        endMinutesAfterMidnight,
      });
      //fahrtenAndUmlaufList.push({umlauf:track,fahrten: perlschnurData.fahrten.filter((f) => f.kurs == track)}); // use == because track might be a string wheras kurs is always a Number
    }
    let { selectedUmlaufIndex, selectedUmlauf, umlaeufeWithFahrten } = this.processUmlaeufe({
      fahrtenAndUmlaufList,
      startMinutesAfterMidnight,
      endMinutesAfterMidnight,
      preselectedUmlauf: umlauf,
    });

    return {
      header: perlschnurData.header, // can be set to display an additional header
      umlaeufe: perlschnurData.umlaeufe,
      umlauf: selectedUmlauf,
      umlaeufeWithFahrten,
      selectedUmlaufIndex,
      fahrtenAndUmlaufList,
    };
  }

  buildUmlaufFahrtenArrayByUsingLinesAndUmlaeufe({ perlschnurData, track, line, startMinutesAfterMidnight, endMinutesAfterMidnight }) {
    let fahrtenAndUmlaufList = [];
    console.debug(
      'buildUmlaufFahrtenArrayByUsingLinesAndUmlaeufe track',
      track,
      'line',
      line,
      'startMinutesAfterMidnight',
      startMinutesAfterMidnight,
      'endMinutesAfterMidnight',
      endMinutesAfterMidnight,
      'perlschnurData',
      perlschnurData
    );
    perlschnurData.umlaeufe.forEach((umlaufObj) => {
      let umlaufFahrten = perlschnurData.fahrten.filter((f) => {
        // original selection logic. Turned out to mix fahrten in whose line is in perlschnur.json
        // This case was described in OP-3758
        // let accept = f.umlauf === umlaufObj.umlauf || f.kurs == track || perlschnurData.linien.includes(f.linie);

        // OP-3758 logic:
        // The new logic which solves OP-3758 is the following,
        // however it is uncertain if this has another side-effect
        return (f.umlauf === umlaufObj.umlauf && f.kurs == track) || (f.linie == line && perlschnurData.linien.includes(f.linie));
      });
      let transformedUmlaufFahrten = this.transformPerlschnurJson(umlaufFahrten, track, startMinutesAfterMidnight, endMinutesAfterMidnight);
      fahrtenAndUmlaufList.push({ umlauf: umlaufObj.umlauf, fahrten: transformedUmlaufFahrten });
    });
    // console.debug('buildUmlaufFahrtenArrayByUsingLinesAndUmlaeufe', fahrtenAndUmlaufList);
    return fahrtenAndUmlaufList;
  }

  /**
   * In case lines are defined the line filter is applied
   * @param perlschnurData
   * @param track
   * @param startMinutesAfterMidnight
   * @param endMinutesAfterMidnight
   * @returns {[{umlauf, fahrten: *[]}]}
   */
  buildUmlaufFahrtenArrayByConcatinatingLines({ perlschnurData, track, startMinutesAfterMidnight, endMinutesAfterMidnight }) {
    let fahrten = perlschnurData.fahrten.filter((f) => perlschnurData.linien.includes(f.linie));
    // console.debug(`buildUmlaufFahrtenArrayByConcatinatingLines`, JSON.stringify(fahrten, null, 2));
    let transformedFahrten = this.transformPerlschnurJson(fahrten, track, startMinutesAfterMidnight, endMinutesAfterMidnight);
    // console.debug(`transformedFahrten`, JSON.stringify(transformedFahrten, null, 2));

    return [{ umlauf: track, fahrten: transformedFahrten }]; // use == because track might be a string wheras kurs is always a Number
  }

  /**
   * In case no Umläufe defined, we filter by track=kurs
   * @param perlschnurData
   * @param track
   * @param startMinutesAfterMidnight
   * @param endMinutesAfterMidnight
   * @returns {[{umlauf, fahrten: *[]}]}
   */
  buildUmlaufFahrtenArrayByFilteringForTrack({ perlschnurData, track, startMinutesAfterMidnight, endMinutesAfterMidnight }) {
    // In case no Umläufe defined, we filter by track=kurs
    let fahrten = perlschnurData.fahrten.filter((f) => f.kurs == track);
    let transformesFahrten = this.transformPerlschnurJson(fahrten, track, startMinutesAfterMidnight, endMinutesAfterMidnight);

    return [{ umlauf: track, fahrten: transformesFahrten }]; // use == because track might be a string wheras kurs is always a Number
  }

  /**
   * Build Fahrten list by applying umlauf filter
   * @param perlschnurData
   * @param startMinutesAfterMidnight
   * @param endMinutesAfterMidnight
   * @returns {*[]}
   */
  buildUmlaufFahrtenArrayFromUmlaeufe({ perlschnurData, track, startMinutesAfterMidnight, endMinutesAfterMidnight }) {
    let fahrtenAndUmlaufList = [];
    perlschnurData.umlaeufe.forEach((umlaufObj) => {
      let umlaufFahrten =
        perlschnurData.fahrten.filter((f) => {
          return f.umlauf === umlaufObj.umlauf;
        }) || [];
      let transformedUmlaufFahrten = this.transformPerlschnurJson(umlaufFahrten, track, startMinutesAfterMidnight, endMinutesAfterMidnight);
      fahrtenAndUmlaufList.push({ umlauf: umlaufObj.umlauf, fahrten: transformedUmlaufFahrten });
    });
    // console.debug('buildUmlaufFahrtenArrayFromUmlaeufe', fahrtenAndUmlaufList);
    return fahrtenAndUmlaufList;
  }

  /**
   * Process the fahrtenAndUmlaufList, that is the following work is performed on each umlauf object of the list:
   * - calculate the includedTracks for all fahrten and store in umlauf object into the includedTracks property
   * - check if first an/ab and last an/ab equals the startMinutesAfterMidnight and endMinutesAfterMidnight params
   *   If so, the isTimeRangeMatch property in each umlauf object is set to true
   * - count umläufe having non-empty fahrten array property
   * @param fahrtenAndUmlaufList
   * @param startMinutesAfterMidnight
   * @param endMinutesAfterMidnight
   * @returns {{umlaeufeWithFahrten: number, fahrtenAndUmlaufList: array, selectedUmlaufIndex: number}}
   */
  processUmlaeufe({ fahrtenAndUmlaufList, startMinutesAfterMidnight, endMinutesAfterMidnight, preselectedUmlauf }) {
    console.debug('processUmlaeufe fahrtenAndUmlaufList.length', fahrtenAndUmlaufList.length);
    let selectedUmlaufIndex = 0;
    let selectedUmlauf;
    let umlaeufeWithFahrten = 0;
    fahrtenAndUmlaufList.forEach((umlaufObj, ind) => {
      let fahrten = umlaufObj.fahrten;

      let includedTracks = {};
      umlaufObj.fahrten.forEach((f) => (includedTracks[f.kurs] = f.kurs));
      umlaufObj.includedTracks = Object.keys(includedTracks);

      if (fahrten && fahrten.length > 0) {
        umlaeufeWithFahrten++;
        let first = fahrten[0];
        let last = fahrten[fahrten.length - 1];

        if (
          (first.ab === startMinutesAfterMidnight || first.an === startMinutesAfterMidnight) &&
          (last.ab === endMinutesAfterMidnight || last.an === endMinutesAfterMidnight)
        ) {
          console.debug('Found timeRangeMatch that is start=startMinutesAfterMidnight and end=endMinutesAfterMidnight ');
          umlaufObj.isTimeRangeMatch = true;
          selectedUmlaufIndex = ind;
          selectedUmlauf = umlaufObj.umlauf;
        }
      }
    });
    if (preselectedUmlauf) {
      selectedUmlauf = preselectedUmlauf;
      selectedUmlaufIndex = fahrtenAndUmlaufList.findIndex((f) => f.umlauf === preselectedUmlauf);
      console.debug('An Umlauf is preselected', preselectedUmlauf, 'return fahrten whose umlauf field equals the preselectedUmlauf');
    } else if (fahrtenAndUmlaufList.length > 1 && !selectedUmlauf) {
      if (!selectedUmlaufIndex) selectedUmlaufIndex = 0;
      if (!selectedUmlauf) selectedUmlauf = fahrtenAndUmlaufList[0].umlauf;
      // umlaeufeWithFahrten = 1
    }

    return { selectedUmlaufIndex, selectedUmlauf, umlaeufeWithFahrten };
  }

  /**
   * Transform the list of Fahrten to an array that can be rendered easily
   * @param fahrtList
   * @param track
   * @param startMinutesAfterMidnight
   * @param endMinutesAfterMidnight
   * @returns {*[]}
   */
  transformPerlschnurJson(fahrtList, track, startMinutesAfterMidnight, endMinutesAfterMidnight) {
    let rows = [];
    let index = 0;
    let previousLastLineOfComment;
    let daAn;
    let daAb;
    const tenant = this.tenant;
    for (let f = 0; f < fahrtList.length; f++) {
      let verlauf = fahrtList[f];
      let anAndAb = { an: verlauf.startMaM, ab: verlauf.endMaM };

      let fahrtDateAsTimeInMs = verlauf.day ? Date.parse(verlauf.day) : null;

      if (tenant === 'lvb') {
        daAn = anAndAb.an;
        daAb = verlauf.haltezeit ? anAndAb.ab - verlauf.haltezeit / 60 : anAndAb.ab;
      } else {
        daAn = anAndAb.an;
        daAb = anAndAb.ab;
      }
      if (startMinutesAfterMidnight <= daAn && daAb <= endMinutesAfterMidnight) {
        let firstNotes = getFirstArrayItem(verlauf.notes);
        let firstHtmlNotes = getFirstArrayItem(verlauf.htmlNotes);
        let firstNote = firstNotes ? firstNotes : firstHtmlNotes;
        let bemerkung;
        if (firstNote && firstNote === previousLastLineOfComment) {
          bemerkung =
            verlauf.notes && verlauf.notes.length > 0
              ? verlauf.notes.slice(1).join('<br>')
              : verlauf.htmlNotes && Array.isArray(verlauf.htmlNotes)
              ? verlauf.htmlNotes.slice(1).join(' ')
              : ''; //correct?
        } else {
          bemerkung =
            verlauf.notes && verlauf.notes.length > 0
              ? verlauf.notes.join('<br>')
              : verlauf.htmlNotes && Array.isArray(verlauf.htmlNotes)
              ? verlauf.htmlNotes.join(' ')
              : ''; //correct?
        }

        let { longname, shortname, zone } = verlauf.ort;
        let anschluss = '';

        let item = {
          // rowId: `V${fahrt.id}_${verlauf.id}`, // unique row id (e.g. for navigating to a special row)
          rowId: `${index}_an-${anAndAb.an}_ab-${anAndAb.ab}`, // unique row id (e.g. for navigating to a special row)
          index,
          isFirstOfVerlauf: verlauf.class === 'fahrtstart',
          fahrtInd: f,
          verlaufInd: f, //used at all?
          fahrtDateAsTimeInMs,
          start: verlauf.class === 'fahrtstart', // indicates if the track has to be marked as "start", that is point with line to next location
          end: verlauf.class === 'fahrtend', // indicates if the track has to be marked as "end", that is point with line from the previous location
          inBetween: true, // correct? v > 0 && v < sortedVerlaeufeOfFahrt.length - 1,
          location: longname, // Haltestelle
          abbreviation: shortname, // Kürzel (d. Haltestelle)
          line: verlauf.linie, // Line
          track: verlauf.kurs, // Kurs
          custom1: verlauf.custom1,
          bgColor: verlauf.bgColor,
          zone, // Zone
          an: anAndAb.an,
          ab: anAndAb.ab,
          comment: bemerkung,
          connection: anschluss, // anschluss
          anFormatted: verlauf.anFormatted,
          abFormatted: verlauf.abFormatted,
        };
        // skip when previous.ab=item.ab
        rows.push(item);
        let lastNotes = getLastArrayItem(verlauf.notes);
        let lastHtmlNotes = getLastArrayItem(verlauf.htmlNotes);
        previousLastLineOfComment = lastNotes ? lastNotes : lastHtmlNotes;
        index++;
      } else {
        // console.debug(
        //   `Skip item since times are out of range. Condition is false: startMinutesAfterMidnight (=${startMinutesAfterMidnight}) <= anAndAb.an(=${anAndAb.an}  && anAndAb.ab (=${anAndAb.ab} <= endMinutesAfterMidnight (${endMinutesAfterMidnight})`
        // );
      }
    }
    // last item should be an end-location. Remove all items from the tail which are marked as start, BUT NOT as inBetween
    let lastRow = rows.length > 0 ? rows[rows.length - 1] : {};
    while (rows.length > 1 && lastRow.start === true && lastRow.inBetween === false) {
      let removed = rows.pop();
      lastRow = rows.length > 0 ? rows[rows.length - 1] : {};
      console.debug('removed row item since not end item', JSON.stringify(removed));
    }
    console.debug('transformPerlschnurJson resulted in ', rows.length);
    return rows;
  }
}

function getFirstArrayItem(arr) {
  if (arr && Array.isArray(arr) && arr.length > 0) {
    return arr[0];
  }
  return null;
}

function getLastArrayItem(arr) {
  if (arr && Array.isArray(arr)) {
    return arr[arr.length - 1];
  }
  return null;
}

export default PerlschnurBuilder;
